import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

import { cn } from './cn';

type IconButtonVariant = 'default' | 'brand' | 'ghost' | 'danger';
type IconButtonSize = 'small' | 'medium' | 'large';

const variantClassNames: Record<IconButtonVariant, string> = {
  default: 'border border-line bg-surface text-ink-muted hover:bg-canvas hover:text-ink',
  brand: 'bg-brand text-ink-inverse hover:bg-brand-hover',
  ghost: 'bg-transparent text-ink-muted hover:bg-brand-subtle hover:text-brand',
  danger: 'bg-transparent text-critical hover:bg-critical/10',
};

const sizeClassNames: Record<IconButtonSize, string> = {
  small: 'size-7',
  medium: 'size-9',
  large: 'size-11',
};

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children: ReactNode;
  label?: string;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    { children, className, label, size = 'medium', title = label, type = 'button', variant = 'default', ...props },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={title}
      className={cn(
        'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-auditor transition-colors',
        // A 44px touch target on coarse pointers without changing the visible size.
        'relative pointer-coarse:after:absolute pointer-coarse:after:top-1/2 pointer-coarse:after:left-1/2 pointer-coarse:after:size-11 pointer-coarse:after:-translate-1/2',
        'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
        sizeClassNames[size],
        variantClassNames[variant],
        className,
      )}
      {...props}
    >
      {children}
    </button>
  ),
);

IconButton.displayName = 'IconButton';
